import { useState, useRef, useEffect, useCallback } from 'react'
import { Upload, FileText, Trash2, Loader2, BookOpen, Globe, Tag, Check, AlertCircle, Database, Eye, X } from 'lucide-react'
import * as XLSX from 'xlsx'
import { rfpApi } from '@/api/client'
import { useAuthStore } from '@/store/authStore'
import { showToast } from '@/store/uiStore'
import type { RfpReferenceDoc, RfpIndexStatus } from '../types'
import { INDEX_STATUS_CONFIG } from '../types'

interface ReferenceUploaderProps {
  projectId?: number
  references: RfpReferenceDoc[]
  onUpdate: () => void
}

interface ParsedSheet {
  sheetName: string
  headers: string[]
  rows: Record<string, string>[]
  rawWs?: XLSX.WorkSheet
}

interface SheetQaMapping {
  selected: boolean
  questionCol: string
  answerCol: string
  sectionCol?: string
  headerRow: number
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function cleanHeaders(raw: string[]): string[] {
  const seen = new Set<string>()
  const result: string[] = []
  for (const h of raw) {
    const s = String(h ?? '').trim()
    if (!s || seen.has(s) || /^__EMPTY/.test(s)) continue
    seen.add(s)
    result.push(s)
  }
  return result
}

function autoDetectQa(headers: string[]): { question: string; answer: string; section?: string } {
  const question = headers.find(h => /requirement|question|description|detail|item/i.test(h)) || ''
  let answer = headers.find(h => /response|answer|reply|comment|vendor/i.test(h)) || ''

  // Fallback: if we found a question column but no answer, use the next column after it
  if (question && !answer) {
    const qIdx = headers.indexOf(question)
    if (qIdx >= 0 && qIdx < headers.length - 1) {
      answer = headers[qIdx + 1]
    }
  }

  return {
    question,
    answer,
    section: headers.find(h => /section|category|group|area|domain/i.test(h)),
  }
}

function parseSheetWithHeaderRow(ws: XLSX.WorkSheet, headerRow: number): { headers: string[]; rows: Record<string, string>[] } {
  // headerRow is 1-based. XLSX range is 0-based, so header index = headerRow - 1
  const jsonData = XLSX.utils.sheet_to_json<Record<string, string>>(ws, { defval: '', range: headerRow - 1 })
  if (jsonData.length === 0) return { headers: [], rows: [] }
  const headers = cleanHeaders(Object.keys(jsonData[0]))
  return { headers, rows: jsonData }
}

function cell(row: Record<string, string>, key: string | undefined): string {
  if (!key) return ''
  try { return String(row[key] ?? '').trim() } catch { return '' }
}

export function ReferenceUploader({ projectId, references, onUpdate }: ReferenceUploaderProps) {
  const { user } = useAuthStore()
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [taggingId, setTaggingId] = useState<number | null>(null)
  const [tagProgress, setTagProgress] = useState<{ tagged: number; total: number; errors: number } | null>(null)
  const [expandedRef, setExpandedRef] = useState<number | null>(null)
  const [qaPairs, setQaPairs] = useState<Array<{ id: number; question: string; answer: string; tags: string | null; tag_status: string; section: string | null }>>([])
  const [loadingQa, setLoadingQa] = useState(false)
  const qaPollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // On mount: if a reference has index_status 'tagging' but the workflow
  // has actually finished (remaining === 0), fix the stale status.
  // Otherwise resume polling for genuinely in-progress tagging.
  useEffect(() => {
    const taggingRef = references.find(r => (r as any).index_status === 'tagging')
    if (taggingRef && !taggingId) {
      // Check if it's genuinely still in progress before resuming
      rfpApi.tagProgress(taggingRef.id).then(res => {
        const d = (res?.data as any)?.data ?? res?.data
        if (d && d.remaining > 0) {
          handleGenerateTags(taggingRef.id)
        } else if (d && d.remaining === 0) {
          onUpdate()
        }
      }).catch(() => { /* ignore */ })
    }
  }, [references]) // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch Q&A pairs when a reference is expanded
  const fetchQaPairs = useCallback(async (refId: number) => {
    try {
      const res = await rfpApi.listQaPairs(refId)
      const data = (res?.data as any)?.data ?? res?.data
      if (Array.isArray(data)) setQaPairs(data)
    } catch { /* ignore */ }
  }, [])

  useEffect(() => {
    if (!expandedRef) {
      setQaPairs([])
      if (qaPollRef.current) { clearInterval(qaPollRef.current); qaPollRef.current = null }
      return
    }
    setLoadingQa(true)
    fetchQaPairs(expandedRef).finally(() => setLoadingQa(false))

    // Auto-refresh Q&A list while tagging is active for this ref
    if (taggingId === expandedRef) {
      qaPollRef.current = setInterval(() => fetchQaPairs(expandedRef), 3000)
    }
    return () => { if (qaPollRef.current) { clearInterval(qaPollRef.current); qaPollRef.current = null } }
  }, [expandedRef, taggingId, fetchQaPairs])

  // Also refresh Q&A when tagging finishes
  useEffect(() => {
    if (!taggingId && expandedRef) fetchQaPairs(expandedRef)
  }, [taggingId, expandedRef, fetchQaPairs])

  // Polling removed — handleGenerateTags now calls tag-batch in a loop directly

  // Column mapping state for new uploads
  const [sheets, setSheets] = useState<ParsedSheet[]>([])
  const [mappings, setMappings] = useState<Record<string, SheetQaMapping>>({})
  const [filename, setFilename] = useState('')
  const [useSheetAsSection, setUseSheetAsSection] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [reindexRefId, setReindexRefId] = useState<number | null>(null)
  const reindexFileRef = useRef<HTMLInputElement>(null)

  const handleReindexFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !reindexRefId) return
    setError(null)
    setFilename(file.name)

    const reader = new FileReader()
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target!.result as ArrayBuffer)
        parseWorkbook(XLSX.read(data, { type: 'array' }))
      } catch {
        setError('Failed to parse Excel file.')
        setReindexRefId(null)
      }
    }
    reader.readAsArrayBuffer(file)
    if (reindexFileRef.current) reindexFileRef.current.value = ''
  }

  const isGlobal = projectId === undefined
  const showMapper = sheets.length > 0

  const parseWorkbook = (wb: XLSX.WorkBook) => {
    const parsed: ParsedSheet[] = []
    const newMappings: Record<string, SheetQaMapping> = {}

    for (const sheetName of wb.SheetNames) {
      try {
        const ws = wb.Sheets[sheetName]
        if (!ws['!ref']) continue // truly empty sheet

        // Auto-detect header row by scanning rows 1-10
        let bestRow = 1
        let bestHeaders: string[] = []
        let bestRows: Record<string, string>[] = []
        for (let tryRow = 1; tryRow <= 10; tryRow++) {
          const result = parseSheetWithHeaderRow(ws, tryRow)
          if (result.headers.length > bestHeaders.length) {
            bestRow = tryRow
            bestHeaders = result.headers
            bestRows = result.rows
          }
          if (result.headers.length >= 2) break // good enough
        }

        if (bestRows.length === 0) continue

        parsed.push({ sheetName, headers: bestHeaders, rows: bestRows, rawWs: ws })
        const auto = autoDetectQa(bestHeaders)
        newMappings[sheetName] = {
          selected: bestHeaders.length >= 2 && !!(auto.question && auto.answer),
          questionCol: auto.question,
          answerCol: auto.answer,
          sectionCol: auto.section,
          headerRow: bestRow,
        }
      } catch {
        console.warn(`Skipping sheet "${sheetName}"`)
      }
    }

    if (parsed.length === 0) {
      setError('No usable data found in any sheet')
      return
    }

    setSheets(parsed)
    setMappings(newMappings)
    setUseSheetAsSection(parsed.length > 1)
  }

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setError(null)
    setFilename(file.name)

    const reader = new FileReader()
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target!.result as ArrayBuffer)
        parseWorkbook(XLSX.read(data, { type: 'array' }))
      } catch {
        setError('Failed to parse Excel file.')
      }
    }
    reader.readAsArrayBuffer(file)
  }


  const handleImportAndIndex = async () => {
    setUploading(true)
    setError(null)

    try {
      const selectedSheets = sheets.filter(s => mappings[s.sheetName]?.selected)

      // Extract Q&A pairs from mapped sheets first
      const pairs: Array<{ row_index: number; section?: string; question: string; answer: string }> = []
      let rowIndex = 0

      for (const sheet of selectedSheets) {
        const m = mappings[sheet.sheetName]
        for (const row of sheet.rows) {
          rowIndex++
          const question = cell(row, m.questionCol)
          const answer = cell(row, m.answerCol)
          if (!question || !answer) continue

          let section: string | undefined
          if (useSheetAsSection) {
            section = sheet.sheetName
          } else if (m.sectionCol) {
            section = cell(row, m.sectionCol) || undefined
          }

          pairs.push({ row_index: rowIndex, section, question, answer })
        }
      }

      if (pairs.length === 0) {
        setError('No Q&A pairs could be extracted with the current column mapping. Check that the question and answer columns are correct and contain data.')
        setUploading(false)
        return
      }

      let refId: number

      if (reindexRefId) {
        // Re-indexing an existing reference — use its ID directly
        refId = reindexRefId
      } else {
        // New upload — build raw text and create reference doc
        let allText = ''
        let totalRows = 0
        for (const sheet of sheets) {
          try {
            allText += `\n=== Sheet: ${sheet.sheetName} ===\n`
            for (const row of sheet.rows) {
              const parts = Object.entries(row)
                .filter(([_, v]) => v && String(v).trim())
                .map(([k, v]) => `${k}: ${String(v).trim()}`)
              if (parts.length > 0) { allText += parts.join(' | ') + '\n'; totalRows++ }
            }
          } catch { /* skip */ }
        }

        const title = filename.replace(/\.(xlsx|xls|csv)$/i, '')
        const uploadRes = await rfpApi.uploadGlobalReference({
          title,
          original_filename: filename,
          content_text: allText,
          row_count: totalRows,
          created_by: user?.email,
          created_by_name: user?.name,
        })

        refId = (uploadRes?.data as any)?.data?.id || (uploadRes?.data as any)?.id
        if (!refId) {
          setError('Upload succeeded but no reference ID returned')
          setUploading(false)
          return
        }
      }

      // Import the extracted Q&A pairs
      await rfpApi.importQaPairs(refId, pairs)
      const label = reindexRefId ? 'Indexed' : 'Uploaded & indexed'
      showToast(`${label} — ${pairs.length} Q&A pairs extracted`, 'success')

      // Reset mapper state
      setSheets([])
      setMappings({})
      setFilename('')
      setReindexRefId(null)
      onUpdate()
    } catch {
      setError('Failed to upload and index reference')
    }
    setUploading(false)
    if (fileRef.current) fileRef.current.value = ''
  }

  const handleGenerateTags = async (refId: number) => {
    setTaggingId(refId)
    setTagProgress({ tagged: 0, total: 0, errors: 0 })
    try {
      let remaining = 1
      let totalTagged = 0
      let totalErrors = 0
      let total = 0
      while (remaining > 0) {
        const res = await rfpApi.tagBatch(refId)
        const d = (res?.data as any)?.data ?? res?.data
        if (!d) break
        totalTagged = d.total_tagged || 0
        total = d.total || 0
        totalErrors += d.batch_errors || 0
        remaining = d.remaining ?? 0
        setTagProgress({ tagged: totalTagged, total, errors: totalErrors })
      }
      showToast(`Tagging complete — ${totalTagged}/${total} tagged${totalErrors ? `, ${totalErrors} errors` : ''}`, totalErrors ? 'warning' : 'success')
      onUpdate()
    } catch {
      showToast('Failed to tag reference', 'error')
    }
    setTaggingId(null)
  }

  const handleDelete = async (refId: number) => {
    await rfpApi.deleteReference(refId)
    showToast('Reference deleted', 'success')
    onUpdate()
  }

  const cancelMapper = () => {
    setSheets([])
    setMappings({})
    setFilename('')
    setError(null)
    setReindexRefId(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  const updateMapping = (sheetName: string, patch: Partial<SheetQaMapping>) => {
    setMappings(prev => ({ ...prev, [sheetName]: { ...prev[sheetName], ...patch } }))
  }

  const changeHeaderRow = (sheetName: string, headerRow: number) => {
    const sheet = sheets.find(s => s.sheetName === sheetName)
    if (!sheet?.rawWs) return
    const { headers, rows } = parseSheetWithHeaderRow(sheet.rawWs, headerRow)
    if (headers.length === 0) return
    // Update the parsed sheet data
    setSheets(prev => prev.map(s => s.sheetName === sheetName ? { ...s, headers, rows } : s))
    // Re-detect columns and update mapping
    const auto = autoDetectQa(headers)
    setMappings(prev => ({
      ...prev,
      [sheetName]: {
        ...prev[sheetName],
        headerRow,
        questionCol: auto.question,
        answerCol: auto.answer,
        sectionCol: auto.section,
        selected: !!(auto.question && auto.answer),
      }
    }))
  }

  const selectedSheets = sheets.filter(s => mappings[s.sheetName]?.selected)
  const allMapped = selectedSheets.length > 0 && selectedSheets.every(s => {
    const m = mappings[s.sheetName]
    return m.questionCol && m.answerCol
  })

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen size={14} className="text-indigo-400" />
          <h3 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider">
            {isGlobal ? 'Sample RFP/RFI Responses' : 'Reference Documents'} ({references.length})
          </h3>
        </div>
        {isGlobal && !showMapper && (
          <div>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} className="hidden" />
            <input ref={reindexFileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleReindexFile} className="hidden" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg bg-indigo-600/20 text-indigo-400 hover:bg-indigo-600/30 transition-colors disabled:opacity-50"
            >
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              Upload Reference
            </button>
          </div>
        )}
      </div>

      {isGlobal && !showMapper && (
        <p className="text-xs text-[var(--text-muted)]">
          Upload past RFI/RFP responses. Map question and answer columns so the AI can index and search them.
        </p>
      )}

      {/* ── Column mapping UI for new upload ── */}
      {showMapper && (
        <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] p-3 space-y-3">
          <div className="flex items-center gap-2">
            <Database size={14} className="text-green-400" />
            <span className="text-xs font-medium text-[var(--text-primary)]">
              {reindexRefId ? `Re-indexing: ${filename}` : filename}
            </span>
            <span className="text-[10px] text-[var(--text-muted)]">{sheets.length} sheets</span>
          </div>

          <p className="text-xs text-[var(--text-muted)]">
            Map the <strong>question</strong> and <strong>answer</strong> columns for each sheet so we can extract Q&A pairs for AI search.
          </p>

          {sheets.length > 1 && (
            <label className="flex items-center gap-2 text-xs text-[var(--text-secondary)] cursor-pointer">
              <input type="checkbox" checked={useSheetAsSection} onChange={e => setUseSheetAsSection(e.target.checked)} className="rounded" />
              Use sheet name as section grouping
            </label>
          )}

          <div className="space-y-2">
            {sheets.map(sheet => {
              const m = mappings[sheet.sheetName]
              if (!m) return null
              const isValid = !!(m.questionCol && m.answerCol)

              return (
                <div key={sheet.sheetName} className={`rounded-lg border overflow-hidden ${m.selected ? 'border-[var(--border-color)]' : 'border-[var(--border-color)] opacity-40'}`}>
                  <div className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-secondary)]">
                    <button
                      onClick={() => updateMapping(sheet.sheetName, { selected: !m.selected })}
                      className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center shrink-0 ${m.selected ? 'bg-indigo-600 border-indigo-600' : 'border-[var(--border-color)]'}`}
                    >
                      {m.selected && <Check size={8} className="text-white" />}
                    </button>
                    <span className={`text-xs font-medium flex-1 ${m.selected ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)] line-through'}`}>
                      {sheet.sheetName}
                    </span>
                    <span className="text-[10px] text-[var(--text-muted)]">{sheet.rows.length} rows</span>
                    {!m.selected && <span className="text-[10px] text-[var(--text-muted)]">Skipped</span>}
                    {m.selected && !isValid && <span className="text-[10px] text-amber-400">Needs mapping</span>}
                    {m.selected && isValid && <span className="text-[10px] text-green-400">Ready</span>}
                  </div>

                  {m.selected && (
                    <div className="px-3 py-2 space-y-2 border-t border-[var(--border-color)]">
                      {/* Header row selector */}
                      <div className="flex items-center gap-2">
                        <label className="text-[10px] text-[var(--text-muted)]">Header row</label>
                        <input
                          type="number"
                          min={1}
                          max={20}
                          value={m.headerRow}
                          onChange={e => {
                            const val = parseInt(e.target.value)
                            if (val >= 1 && val <= 20) changeHeaderRow(sheet.sheetName, val)
                          }}
                          className="w-14 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-0.5 text-xs text-[var(--text-primary)] text-center focus:outline-none focus:border-[var(--accent-color)]"
                        />
                        <span className="text-[10px] text-[var(--text-muted)]">
                          {sheet.headers.length} columns · {sheet.rows.length} rows
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] text-[var(--text-muted)] mb-0.5 block">Question Column <span className="text-red-400">*</span></label>
                          <select value={m.questionCol} onChange={e => updateMapping(sheet.sheetName, { questionCol: e.target.value })}
                            className="w-full rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-1 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-color)]">
                            <option value="">Select...</option>
                            {sheet.headers.map(h => <option key={h} value={h}>{h}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] text-[var(--text-muted)] mb-0.5 block">Answer Column <span className="text-red-400">*</span></label>
                          <select value={m.answerCol} onChange={e => updateMapping(sheet.sheetName, { answerCol: e.target.value })}
                            className="w-full rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-1 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-color)]">
                            <option value="">Select...</option>
                            {sheet.headers.map(h => <option key={h} value={h}>{h}</option>)}
                          </select>
                        </div>
                      </div>
                      {!useSheetAsSection && (
                        <div>
                          <label className="text-[10px] text-[var(--text-muted)] mb-0.5 block">Section Column</label>
                          <select value={m.sectionCol || ''} onChange={e => updateMapping(sheet.sheetName, { sectionCol: e.target.value || undefined })}
                            className="w-full rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-1 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-color)]">
                            <option value="">None</option>
                            {sheet.headers.map(h => <option key={h} value={h}>{h}</option>)}
                          </select>
                        </div>
                      )}
                      {/* Preview */}
                      {isValid && (
                        <div className="space-y-1">
                          <p className="text-[9px] text-[var(--text-muted)] uppercase tracking-wider font-semibold">Preview</p>
                          {sheet.rows.slice(0, 2).map((row, i) => (
                            <div key={i} className="text-[10px] bg-[var(--bg-secondary)] rounded p-1.5 border border-[var(--border-color)]">
                              <span className="text-[var(--text-muted)]">Q:</span>{' '}
                              <span className="text-[var(--text-primary)]">{cell(row, m.questionCol).substring(0, 80)}</span>
                              <br />
                              <span className="text-[var(--text-muted)]">A:</span>{' '}
                              <span className="text-[var(--text-secondary)]">{cell(row, m.answerCol).substring(0, 80)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {error && (
            <div className="flex items-center gap-2 text-xs text-red-400">
              <AlertCircle size={12} />{error}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button onClick={cancelMapper} className="px-2.5 py-1.5 text-xs rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors">
              Cancel
            </button>
            <button onClick={handleImportAndIndex} disabled={!allMapped || uploading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 transition-colors disabled:opacity-50">
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <Database size={12} />}
              Import & Index Q&A Pairs
            </button>
          </div>
        </div>
      )}

      {/* ── Reference list ── */}
      {references.length === 0 && !showMapper ? (
        <div className="text-center py-6 rounded-lg border border-dashed border-[var(--border-color)]">
          <FileText size={20} className="mx-auto mb-2 text-[var(--text-muted)]" />
          <p className="text-xs text-[var(--text-muted)]">
            {isGlobal ? 'No reference documents yet. Upload past RFI/RFP responses to train the AI.' : 'No reference documents available.'}
          </p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {references.map(ref => {
            const refIsGlobal = ref.project_id === null
            const indexStatus = (ref as any).index_status as RfpIndexStatus || 'raw'
            const qaCount = (ref as any).qa_count || 0
            const statusCfg = INDEX_STATUS_CONFIG[indexStatus] || INDEX_STATUS_CONFIG.raw
            const isTagging = taggingId === ref.id

            return (
              <div key={ref.id} className={`flex items-center justify-between p-2.5 rounded-lg border ${indexStatus === 'raw' ? 'bg-amber-500/5 border-amber-500/20' : 'bg-[var(--bg-primary)] border-[var(--border-color)]'}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <FileText size={14} className={indexStatus === 'raw' ? 'text-amber-400 shrink-0' : 'text-indigo-400 shrink-0'} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="text-xs font-medium text-[var(--text-primary)] truncate">{ref.title}</p>
                      {!isGlobal && refIsGlobal && (
                        <span className="flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-medium bg-indigo-500/10 text-indigo-400 shrink-0">
                          <Globe size={8} />Global
                        </span>
                      )}
                      <span className={`text-[9px] font-medium ${statusCfg.color}`}>{statusCfg.label}</span>
                      {qaCount > 0 && (
                        <span className="text-[9px] text-[var(--text-muted)]">{qaCount} Q&A pairs</span>
                      )}
                    </div>
                    <p className="text-[10px] text-[var(--text-muted)]">
                      {indexStatus === 'raw'
                        ? 'Needs column mapping — re-upload the Excel file to map question & answer columns'
                        : `${ref.row_count} rows — ${formatBytes((ref as any).content_length || 0)}${ref.created_by_name ? ` — by ${ref.created_by_name}` : ''}`
                      }
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {/* View Q&A button */}
                  {qaCount > 0 && (
                    <button
                      onClick={() => setExpandedRef(ref.id)}
                      className="flex items-center gap-1 px-2 py-1 text-[10px] rounded bg-[var(--bg-secondary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                    >
                      <Eye size={10} />
                      View
                    </button>
                  )}
                  {/* Raw references — re-upload Excel to map columns properly */}
                  {isGlobal && indexStatus === 'raw' && !showMapper && (
                    <button
                      onClick={() => { setReindexRefId(ref.id); reindexFileRef.current?.click() }}
                      className="flex items-center gap-1 px-2 py-1 text-[10px] rounded bg-indigo-600/20 text-indigo-400 hover:bg-indigo-600/30 transition-colors"
                    >
                      <Database size={10} />
                      Index
                    </button>
                  )}
                  {/* Tag button / progress */}
                  {isGlobal && isTagging && tagProgress && (
                    <span className="flex items-center gap-1.5 px-2 py-1 text-[10px] rounded bg-amber-600/20 text-amber-400">
                      <Loader2 size={10} className="animate-spin" />
                      {tagProgress.tagged}/{tagProgress.total}
                      {tagProgress.errors > 0 && <span className="text-red-400">({tagProgress.errors} errors)</span>}
                    </span>
                  )}
                  {isGlobal && !isTagging && indexStatus === 'tagging' && qaCount > 0 && (
                    <span className="flex items-center gap-1.5 px-2 py-1 text-[10px] rounded bg-amber-600/20 text-amber-400">
                      <Loader2 size={10} className="animate-spin" />
                      Processing...
                    </span>
                  )}
                  {isGlobal && !isTagging && (indexStatus === 'indexed' || indexStatus === 'error') && qaCount > 0 && (
                    <button
                      onClick={() => handleGenerateTags(ref.id)}
                      disabled={!!taggingId}
                      className="flex items-center gap-1 px-2 py-1 text-[10px] rounded bg-amber-600/20 text-amber-400 hover:bg-amber-600/30 transition-colors disabled:opacity-50"
                      title="Generate AI search tags for Q&A pairs"
                    >
                      <Tag size={10} />
                      Tag
                    </button>
                  )}
                  {isGlobal && (
                    <button onClick={() => handleDelete(ref.id)} className="p-1 text-[var(--text-muted)] hover:text-red-400 transition-colors">
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* ── Q&A Viewer Modal ── */}
      {expandedRef && (() => {
        const ref = references.find(r => r.id === expandedRef)
        const isTaggingThis = taggingId === expandedRef
        const taggedCount = qaPairs.filter(q => q.tag_status === 'tagged').length
        const pendingCount = qaPairs.filter(q => q.tag_status === 'pending').length
        const errorCount = qaPairs.filter(q => q.tag_status === 'error').length

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setExpandedRef(null)}>
            <div className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-xl w-[90vw] max-w-4xl max-h-[85vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-color)]">
                <div>
                  <h2 className="text-sm font-semibold text-[var(--text-primary)]">{ref?.title || 'Q&A Pairs'}</h2>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-[var(--text-muted)]">{qaPairs.length} pairs</span>
                    {taggedCount > 0 && <span className="text-xs text-green-400">{taggedCount} tagged</span>}
                    {pendingCount > 0 && <span className="text-xs text-[var(--text-muted)]">{pendingCount} pending</span>}
                    {errorCount > 0 && <span className="text-xs text-red-400">{errorCount} errors</span>}
                    {isTaggingThis && (
                      <span className="flex items-center gap-1 text-xs text-amber-400">
                        <Loader2 size={10} className="animate-spin" />
                        Tagging in progress...
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={() => setExpandedRef(null)} className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-colors">
                  <X size={16} />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto">
                {loadingQa ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 size={20} className="animate-spin text-[var(--text-muted)]" />
                  </div>
                ) : qaPairs.length === 0 ? (
                  <p className="text-sm text-[var(--text-muted)] text-center py-12">No Q&A pairs found</p>
                ) : (
                  <table className="w-full text-left">
                    <thead className="sticky top-0 bg-[var(--bg-secondary)] z-10">
                      <tr>
                        <th className="px-3 py-2 text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider w-8">#</th>
                        <th className="px-3 py-2 text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider w-20">Status</th>
                        <th className="px-3 py-2 text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider">Question</th>
                        <th className="px-3 py-2 text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider">Answer</th>
                        <th className="px-3 py-2 text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider w-48">Tags</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--border-color)]">
                      {qaPairs.map((qa, i) => (
                        <tr key={qa.id} className="hover:bg-[var(--bg-secondary)] transition-colors">
                          <td className="px-3 py-2 text-[10px] text-[var(--text-muted)] font-mono align-top">{i + 1}</td>
                          <td className="px-3 py-2 align-top">
                            {qa.tag_status === 'tagged' && <span className="flex items-center gap-1 text-[10px] text-green-400"><Check size={10} />Tagged</span>}
                            {qa.tag_status === 'pending' && <span className="text-[10px] text-[var(--text-muted)]">Pending</span>}
                            {qa.tag_status === 'error' && <span className="text-[10px] text-red-400">Error</span>}
                          </td>
                          <td className="px-3 py-2 text-[11px] text-[var(--text-primary)] align-top max-w-xs">
                            {qa.section && <span className="text-[9px] px-1 py-0.5 rounded bg-[var(--bg-secondary)] text-[var(--text-muted)] mr-1">{qa.section}</span>}
                            {qa.question.substring(0, 120)}{qa.question.length > 120 ? '...' : ''}
                          </td>
                          <td className="px-3 py-2 text-[11px] text-[var(--text-secondary)] align-top max-w-xs">
                            {qa.answer.substring(0, 120)}{qa.answer.length > 120 ? '...' : ''}
                          </td>
                          <td className="px-3 py-2 align-top">
                            {qa.tags ? (
                              <div className="flex flex-wrap gap-1">
                                {qa.tags.split(',').map(tag => tag.trim()).filter(Boolean).map(tag => (
                                  <span key={tag} className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-indigo-500/10 text-indigo-400">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-[10px] text-[var(--text-muted)]">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}
